import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Calendar,
  Droplet,
  Heart,
  Share2,
  User,
  Hospital,
  XCircle,
  Loader,
  MapPin as LocationIcon,
  Search,
  Filter,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  Users,
  Navigation,
  Phone,
  Mail,
  PhoneCall,
  X
} from 'lucide-react';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const AvailableRequests = ({ handleAcceptRequest }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [availableRequests, setAvailableRequests] = useState([]);
  const [acceptedRequestIds, setAcceptedRequestIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [filters, setFilters] = useState({
    bloodType: '',
    urgency: '',
    date: new Date().toISOString().split('T')[0] // Default to current date
  });
  const [donorBloodType, setDonorBloodType] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [donorProfile, setDonorProfile] = useState(null);

  // Get current location when component mounts
  useEffect(() => {
    // Automatically get location on component mount
    getCurrentLocation();
    fetchAcceptedRequests();
    fetchDonorProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch available requests when filters or location changes
  useEffect(() => {
    if (currentLocation) {
      fetchAvailableRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentLocation, acceptedRequestIds, donorProfile]);

  // Fetch donor profile to get state, city, and blood type
  const fetchDonorProfile = useCallback(async () => {
    try {
      // First try to get detailed profile
      let profileData = null;

      try {
        const detailedResponse = await api.get('/auth/detailed-profile');
        profileData = detailedResponse.data.profile;
        console.log('Detailed profile data loaded successfully');
      } catch (detailedError) {
        console.log('Detailed profile endpoint not available, falling back to regular endpoints');

        // If detailed profile fails, fall back to regular donor endpoint
        const response = await api.get('/donors/me', {
          headers: {
            'x-auth-token': token
          }
        });
        profileData = response.data;
      }

      console.log('Fetched donor profile:', profileData);
      setDonorProfile(profileData);

      // Set donor's blood type
      if (profileData?.bloodType) {
        setDonorBloodType(profileData.bloodType);
        // Set blood type as default filter
        setFilters(prev => ({
          ...prev,
          bloodType: profileData.bloodType
        }));
      }
    } catch (error) {
      console.error('Error fetching donor profile:', error);
      // Continue with available requests even if this fails
    }
  }, [token]);

  // Fetch the donor's accepted requests
  const fetchAcceptedRequests = useCallback(async () => {
    try {
      const response = await api.get('/requests/accepted', {
        headers: {
          'x-auth-token': token
        }
      });

      console.log('Fetched accepted requests:', response.data);

      // Extract the request IDs from the accepted requests
      const acceptedIds = response.data.map(req =>
        req.requestId || req.request || req._id
      );

      setAcceptedRequestIds(acceptedIds);
    } catch (error) {
      // If 404, it just means no accepted requests found, which is fine
      if (error.response && error.response.status === 404) {
        console.log('No accepted requests found.');
        setAcceptedRequestIds([]);
        return;
      }
      console.error('Error fetching accepted requests:', error);
      // Continue with available requests even if this fails
    }
  }, [token]);

  // Get current location
  const locationRequestRef = React.useRef(false);

  const getCurrentLocation = useCallback(() => {
    // Prevent duplicate location requests using ref which persists better than state during fast updates
    if (locationRequestRef.current) {
      return;
    }

    locationRequestRef.current = true;
    setLocationRequested(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setCurrentLocation({ lat: latitude, lng: longitude });
          console.log("Location set:", { lat: latitude, lng: longitude });
          toast.success('Location updated successfully');
          // No need to call fetchAvailableRequests() here as useEffect will trigger it when currentLocation changes
        },
        error => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your current location. Some features may be limited.');
          // Use a default location (e.g., Mumbai)
          setCurrentLocation({ lat: 19.0760, lng: 72.8777 });
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      // Use a default location
      setCurrentLocation({ lat: 19.0760, lng: 72.8777 });
    }
  }, []);

  // Fetch available requests
  const fetchAvailableRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all blood requests from the backend
      const response = await api.get('/requests', {
        headers: {
          'x-auth-token': token
        }
      });

      console.log('Fetched requests:', response.data);

      // Apply filters on the client side
      let filteredRequests = response.data;

      // Filter by blood type if specified
      if (filters.bloodType) {
        filteredRequests = filteredRequests.filter(req => req.bloodType === filters.bloodType);
      }

      // Filter by urgency if specified
      if (filters.urgency) {
        filteredRequests = filteredRequests.filter(req => req.urgency === filters.urgency);
      }

      // Filter by date if specified
      if (filters.date) {
        const selectedDate = new Date(filters.date);
        const selectedDateStr = selectedDate.toISOString().split('T')[0];

        filteredRequests = filteredRequests.filter(req => {
          const requestDate = new Date(req.createdAt);
          const requestDateStr = requestDate.toISOString().split('T')[0];
          return requestDateStr === selectedDateStr;
        });
      }

      // Filter out requests that have already been accepted by this donor
      if (acceptedRequestIds.length > 0) {
        filteredRequests = filteredRequests.filter(req =>
          !acceptedRequestIds.includes(req._id)
        );
      }

      // Filter out requests created by the current user
      if (user && user.id) {
        console.log('Filtering out requests created by user:', user.id);
        filteredRequests = filteredRequests.filter(req => {
          // Check all possible user ID fields
          const requesterId = req.requester || req.requesterId || req.user || req.userId;
          const isOwnRequest = requesterId === user.id ||
            (typeof requesterId === 'object' && requesterId._id === user.id);

          console.log(`Request ${req._id} created by ${requesterId}, isOwnRequest: ${isOwnRequest}`);
          return !isOwnRequest;
        });
      }

      // Filter out requests that are completed (have verified donations)
      filteredRequests = filteredRequests.filter(req => {
        // Check if any donor has verified their donation
        const hasVerifiedDonation = req.donors && req.donors.some(donor =>
          donor.status === 'donated' || donor.status === 'verified'
        );

        // Keep the request if it doesn't have any verified donations
        return !hasVerifiedDonation;
      });

      // Filter requests by user's state and city if donor profile is available
      if (donorProfile && donorProfile.address) {
        const userState = donorProfile.address.state;
        const userCity = donorProfile.address.city;

        if (userState && userCity) {
          console.log(`Filtering requests by user's location: ${userCity}, ${userState}`);
          filteredRequests = filteredRequests.filter(req => {
            const requestState = req.hospital?.state;
            const requestCity = req.hospital?.city;

            // Check if state and city exist and match
            return requestState && requestCity &&
              requestState.toLowerCase() === userState.toLowerCase() &&
              requestCity.toLowerCase() === userCity.toLowerCase();
          });
        }
      }

      // Transform the data to match the expected format
      const formattedRequests = filteredRequests
        .map(req => {
          // Format address to avoid duplication
          const hospitalAddress = req.hospital?.address || 'N/A';
          const city = req.hospital?.city || '';
          const state = req.hospital?.state || '';

          // Create a clean, non-duplicated address
          let formattedAddress = hospitalAddress;
          let formattedLocation = '';

          if (city) {
            formattedLocation += city;
          }

          if (state) {
            formattedLocation += formattedLocation ? `, ${state}` : state;
          }

          // Calculate distance if both coordinates are available
          let distance = null;
          if (currentLocation && req.location?.coordinates) {
            distance = calculateDistance(
              currentLocation.lat,
              currentLocation.lng,
              req.location.coordinates[1],
              req.location.coordinates[0]
            );
          }

          return {
            requestId: req._id,
            bloodGroup: req.bloodType,
            requesterName: req.patientName,
            state: state,
            city: city,
            location: formattedLocation,
            status: req.status.charAt(0).toUpperCase() + req.status.slice(1),
            hospitalName: req.hospital?.name || 'N/A',
            urgencyLevel: req.urgency === 'emergency' ? 'Critical' :
              req.urgency === 'urgent' ? 'High' : 'Normal',
            unitsNeeded: req.unitsNeeded,
            requestDate: formatDateWithTimeWindow(req.createdAt),
            createdAt: new Date(req.createdAt), // Add createdAt for sorting
            address: formattedAddress,
            donors: req.donors || [],
            coordinates: req.location?.coordinates ? {
              lng: req.location.coordinates[0],
              lat: req.location.coordinates[1]
            } : null,
            distance: distance,
            contactPhone: req.contactInfo?.phone || null,
            contactName: req.contactInfo?.name || req.patientName
          };
        })
        .sort((a, b) => b.createdAt - a.createdAt); // Sort by date, newest first

      setAvailableRequests(formattedRequests);
    } catch (error) {
      console.error('Error fetching available requests:', error);
      setError('Failed to load available blood requests');

      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        // Redirect to login page or handle as needed
        return;
      }

      toast.error('Failed to load available blood requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, currentLocation, acceptedRequestIds, filters, user, donorProfile]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;

    console.log("Calculating distance between:", { lat1, lon1, lat2, lon2 });

    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in km
    return distance.toFixed(1);
  };

  const formatDateWithTimeWindow = (dateString) => {
    const date = new Date(dateString);

    // Format the date
    const formattedDate = date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    // Format the time
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    // Calculate time window (1 hour later)
    const endDate = new Date(date);
    endDate.setHours(endDate.getHours() + 1);
    const endHours = endDate.getHours();
    const endMinutes = endDate.getMinutes();
    const formattedEndTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

    return `${formattedDate}, ${formattedTime} - ${formattedEndTime}`;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearBloodTypeFilter = () => {
    setFilters(prev => ({
      ...prev,
      bloodType: ''
    }));
  };

  const setToMyBloodType = () => {
    if (donorBloodType) {
      setFilters(prev => ({
        ...prev,
        bloodType: donorBloodType
      }));
    }
  };

  const handleDateChange = (e) => {
    setFilters(prev => ({
      ...prev,
      date: e.target.value
    }));
  };

  const setToToday = () => {
    setFilters(prev => ({
      ...prev,
      date: new Date().toISOString().split('T')[0]
    }));
  };

  const clearDateFilter = () => {
    setFilters(prev => ({
      ...prev,
      date: ''
    }));
  };

  const handleAcceptClick = async (request) => {
    setSelectedRequest(request);
    setShowConfirmDialog(true);
    setConfirmChecked(false);
  };

  const handleConfirmAccept = async () => {
    if (!confirmChecked) {
      toast.error('Please confirm that you want to accept this request');
      return;
    }

    try {
      setShowConfirmDialog(false);

      // Show loading toast
      toast.loading('Processing your request...', {
        id: 'accept-request-toast'
      });

      // Call the API to accept the request with proper headers and data
      await api.post(`/requests/${selectedRequest.requestId}/accept`, {
        acceptedDate: new Date().toISOString() // Add date information
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });

      // Add the request ID to accepted requests
      setAcceptedRequestIds(prev => [...prev, selectedRequest.requestId]);

      // Show enhanced success popup with Toastify
      toast.success(
        <div className="text-center">
          <div className="font-bold text-lg mb-2">Request Accepted Successfully! 🎉</div>
          <div className="mb-2">You have accepted to donate:</div>
          <div className="font-semibold text-red-600 mb-2">
            {selectedRequest.unitsNeeded} units of {selectedRequest.bloodGroup} blood
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium">Hospital:</span> {selectedRequest.hospitalName}
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium">Location:</span> {selectedRequest.location}
          </div>
          <div className="text-sm mb-1">
            <span className="font-medium">Patient:</span> {selectedRequest.requesterName}
          </div>
          {selectedRequest.contactPhone && (
            <div className="text-sm mb-1">
              <span className="font-medium">Contact:</span> {selectedRequest.contactPhone}
            </div>
          )}
          <div className="text-sm font-medium text-gray-600">
            Redirecting to accepted requests...
          </div>
        </div>,
        {
          id: 'accept-request-toast',
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: {
            background: '#ffffff',
            color: '#111827',
            borderLeft: '6px solid #dc2626',
            padding: '16px',
            minWidth: '320px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }
        }
      );

      // Wait for 3 seconds before redirecting
      setTimeout(() => {
        navigate('/dashboard/accepted');
      }, 3000);

    } catch (error) {
      console.error('Error accepting request:', error);

      // Get the error message from the response
      const errorMessage = error.response?.data?.message ||
        'An error occurred while accepting the request';

      // Check for specific error conditions
      let displayMessage = errorMessage;

      if (error.response?.status === 400 && errorMessage.includes('already responded')) {
        displayMessage = 'You have already accepted this blood request.';
      } else if (error.response?.status === 400 && errorMessage.includes('maximum number')) {
        displayMessage = 'This request already has the maximum number of donors and cannot accept more.';
      } else if (error.response?.status === 500) {
        displayMessage = 'Server error. Please try again later.';
      }

      toast.error(
        <div className="text-center">
          <div className="font-bold text-lg mb-1">Failed to Accept Request</div>
          <div>{displayMessage}</div>
        </div>,
        {
          id: 'accept-request-toast'
        }
      );
    }
  };

  // Handle phone call
  const handleCallPatient = (phoneNumber, patientName) => {
    if (!phoneNumber) {
      toast.error('Phone number not available for this patient');
      return;
    }

    // Format phone number for calling
    const formattedPhone = phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');

    // Check if it's a valid phone number
    if (formattedPhone.length < 10) {
      toast.error('Invalid phone number format');
      return;
    }

    // Show confirmation dialog
    if (window.confirm(`Call ${patientName} at ${phoneNumber}?`)) {
      // Use tel: protocol to initiate call
      window.location.href = `tel:${formattedPhone}`;

      // Show success message
      toast.success(`Initiating call to ${patientName}...`);
    }
  };

  return (
    <div className="relative">
      <ToastContainer />
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
          >
            <h3 className="text-xl font-bold mb-4 text-gray-900">Confirm Request Acceptance</h3>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">You are about to accept the following blood donation request:</p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="mb-2"><span className="font-medium">Blood Group:</span> {selectedRequest.bloodGroup}</p>
                <p className="mb-2"><span className="font-medium">Units Needed:</span> {selectedRequest.unitsNeeded}</p>
                <p className="mb-2"><span className="font-medium">Hospital:</span> {selectedRequest.hospitalName}</p>
                <p className="mb-2"><span className="font-medium">Location:</span> {selectedRequest.location}</p>
                {selectedRequest.contactPhone && (
                  <p className="mb-2">
                    <span className="font-medium">Contact:</span> {selectedRequest.contactName}
                    <br />
                    <span className="font-medium">Phone:</span> {selectedRequest.contactPhone}
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that I want to accept this blood donation request and I will be available to donate.
                </span>
              </label>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmAccept}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 ${confirmChecked
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                disabled={!confirmChecked}
              >
                Accept Request
              </button>
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 bg-gray-100 text-gray-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-3xl shadow-2xl p-4 relative overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
        </div>

        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
            <div className="flex-1 w-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white mb-1">Available Blood Requests</h1>
                  <p className="text-red-100 text-sm">
                    {donorProfile && donorProfile.address && donorProfile.address.city && donorProfile.address.state ?
                      `Showing requests in ${donorProfile.address.city}, ${donorProfile.address.state}` :
                      'Find and respond to blood donation requests in your area'}
                    {donorBloodType && (
                      <span className="block mt-1 text-red-200">
                        Filtered by your blood type: <span className="font-semibold">{donorBloodType}</span>
                      </span>
                    )}
                    {filters.date && (
                      <span className="block mt-1 text-red-200">
                        Showing requests from: <span className="font-semibold">
                          {new Date(filters.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-col w-full lg:w-auto gap-3">
              <div className="flex flex-wrap gap-2 w-full">
                {/* Date Picker */}
                <div className="relative">
                  <input
                    type="date"
                    value={filters.date}
                    onChange={handleDateChange}
                    className="px-3 py-1.5 backdrop-blur-sm border border-white/20 rounded-xl text-white text-xs focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200 cursor-pointer hover:bg-white/10"
                    style={{ colorScheme: 'dark' }}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/70 pointer-events-none" />
                </div>

                {/* Today Button */}
                <motion.button
                  onClick={setToToday}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl text-xs font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Calendar className="w-4 h-4" />
                  Today
                </motion.button>

                {/* Clear Date Button */}
                {filters.date && (
                  <motion.button
                    onClick={clearDateFilter}
                    className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl text-xs font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <X className="w-4 h-4" />
                    All Dates
                  </motion.button>
                )}

                <div className="relative">
                  <select
                    name="bloodType"
                    value={filters.bloodType}
                    onChange={handleFilterChange}
                    className={`px-3 py-1.5 backdrop-blur-sm border rounded-xl text-xs focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200 ${filters.bloodType === donorBloodType
                      ? 'bg-green-500/20 border-green-300 text-white'
                      : 'bg-white/10 border-white/20 text-white'
                      }`}
                  >
                    <option value="" className="text-gray-800">All Blood Types</option>
                    <option value="A+" className="text-gray-800">A+</option>
                    <option value="A-" className="text-gray-800">A-</option>
                    <option value="B+" className="text-gray-800">B+</option>
                    <option value="B-" className="text-gray-800">B-</option>
                    <option value="O+" className="text-gray-800">O+</option>
                    <option value="O-" className="text-gray-800">O-</option>
                    <option value="AB+" className="text-gray-800">AB+</option>
                    <option value="AB-" className="text-gray-800">AB-</option>
                  </select>
                  {filters.bloodType === donorBloodType && donorBloodType && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                {donorBloodType && (
                  <motion.button
                    onClick={filters.bloodType === donorBloodType ? clearBloodTypeFilter : setToMyBloodType}
                    className={`px-3 py-1.5 backdrop-blur-sm border rounded-xl text-xs font-medium transition-all duration-200 flex items-center gap-2 ${filters.bloodType === donorBloodType
                      ? 'bg-red-500/20 border-red-300 text-white hover:bg-red-500/30'
                      : 'bg-green-500/20 border-green-300 text-white hover:bg-green-500/30'
                      }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={filters.bloodType === donorBloodType ? "Show all blood types" : `Show only ${donorBloodType} requests`}
                  >
                    {filters.bloodType === donorBloodType ? (
                      <>
                        <Droplet className="w-4 h-4" />
                        All
                      </>
                    ) : (
                      <>
                        <Heart className="w-4 h-4" />
                        {donorBloodType}
                      </>
                    )}
                  </motion.button>
                )}
                <select
                  name="urgency"
                  value={filters.urgency}
                  onChange={handleFilterChange}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white text-xs focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                >
                  <option value="" className="text-gray-800">All Urgency</option>
                  <option value="emergency" className="text-gray-800">Critical</option>
                  <option value="urgent" className="text-gray-800">High</option>
                  <option value="normal" className="text-gray-800">Normal</option>
                </select>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <motion.button
                  onClick={getCurrentLocation}
                  className="flex-1 sm:flex-none justify-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl text-xs font-medium hover:bg-white/20 transition-all duration-200 flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Navigation className="w-4 h-4" />
                  Use Location
                </motion.button>
                <motion.button
                  onClick={fetchAvailableRequests}
                  className="flex-1 sm:flex-none justify-center px-3 py-1.5 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-xl text-xs font-medium hover:bg-white/30 transition-all duration-200 flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Active Filters Display */}
      {(filters.date || filters.bloodType || filters.urgency) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl flex items-center gap-3 shadow-sm mt-4"
        >
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
            <Filter className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">Active Filters</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {filters.date && (
                <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded-lg text-xs font-medium flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(filters.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              )}
              {filters.bloodType && (
                <span className="px-2 py-1 bg-red-200 text-red-800 rounded-lg text-xs font-medium flex items-center gap-1">
                  <Droplet className="w-3 h-3" />
                  {filters.bloodType}
                </span>
              )}
              {filters.urgency && (
                <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded-lg text-xs font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {filters.urgency === 'emergency' ? 'Critical' :
                    filters.urgency === 'urgent' ? 'High' : 'Normal'}
                </span>
              )}
            </div>
          </div>
          <motion.button
            onClick={() => setFilters({ bloodType: '', urgency: '', date: new Date().toISOString().split('T')[0] })}
            className="px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-all duration-200 flex items-center gap-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <X className="w-4 h-4" />
            Clear All
          </motion.button>
        </motion.div>
      )}

      {/* Location Status */}
      <AnimatePresence>
        {currentLocation && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3 shadow-sm"
          >
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-semibold text-sm">Location Active</p>
              <p className="text-xs text-green-600">Using your current location for better results</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3 shadow-sm"
          >
            <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center h-64"
        >
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl flex items-center justify-center mb-4">
              <Loader className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-2">Loading Requests</p>
            <p className="text-gray-500">Finding blood donation requests in your area...</p>
          </div>
        </motion.div>
      ) : (
        <>
          {/* Requests Grid */}
          {availableRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {availableRequests.map((request, index) => (
                <motion.div
                  key={request.requestId || index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  {/* Header with Blood Group and Urgency */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${request.urgencyLevel === 'Critical' ? 'bg-gradient-to-br from-red-500 to-red-600' :
                          request.urgencyLevel === 'High' ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
                            'bg-gradient-to-br from-blue-500 to-blue-600'
                          }`}>
                          <Droplet className="text-white w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{request.bloodGroup}</h3>
                          <p className="text-gray-600 font-medium text-sm">{request.unitsNeeded} units needed</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${request.urgencyLevel === 'Critical' ? 'bg-red-100 text-red-700 border border-red-200' :
                          request.urgencyLevel === 'High' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                            'bg-blue-100 text-blue-700 border border-blue-200'
                          }`}>
                          {request.urgencyLevel}
                        </span>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <Users className="w-3.5 h-3.5" />
                          <span>{request.donors ? request.donors.length : 0}/3 donors</span>
                        </div>
                      </div>
                    </div>

                    {/* Requester Info */}
                    <div className="space-y-2.5 mb-4">
                      <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-xl">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Patient</p>
                          <p className="font-semibold text-gray-900 text-sm">{request.requesterName}</p>
                          {request.contactPhone ? (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Phone className="w-3 h-3 text-gray-500" />
                              <span className="text-xs text-gray-600">{request.contactPhone}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400 italic">No phone available</span>
                            </div>
                          )}
                        </div>
                        {request.contactPhone ? (
                          <motion.button
                            onClick={() => handleCallPatient(request.contactPhone, request.contactName)}
                            className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title={`Call ${request.contactName}`}
                          >
                            <PhoneCall className="w-4 h-4" />
                          </motion.button>
                        ) : (
                          <button
                            disabled
                            className="w-8 h-8 bg-gray-200 text-gray-400 rounded-lg flex items-center justify-center cursor-not-allowed"
                            title="No phone number available"
                          >
                            <PhoneCall className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-xl">
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                          <Hospital className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Hospital</p>
                          <p className="font-semibold text-gray-900 text-sm">{request.hospitalName}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-xl">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mt-0.5">
                          <MapPin className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Location</p>
                          <p className="font-semibold text-gray-900 text-sm">{request.address}</p>
                          <p className="text-gray-500 text-xs">{request.location}</p>
                        </div>
                      </div>

                      {request.distance && (
                        <div className="flex items-center gap-2.5 p-2.5 bg-blue-50 rounded-xl border border-blue-200">
                          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                            <Navigation className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Distance</p>
                            <p className="font-semibold text-blue-700 text-sm">{request.distance} km away</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-xl">
                        <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                          <Clock className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Requested</p>
                          <p className="font-semibold text-gray-900 text-sm">{request.requestDate}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {request.donors && request.donors.length < 3 ? (
                        <motion.button
                          onClick={() => handleAcceptClick(request)}
                          className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-2.5 px-4 rounded-xl text-sm font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Heart className="w-4 h-4" />
                          Accept Request
                        </motion.button>
                      ) : (
                        <button
                          disabled
                          className="flex-1 bg-gray-200 text-gray-500 py-2.5 px-4 rounded-xl text-sm font-semibold cursor-not-allowed"
                        >
                          Maximum Donors Reached (3/3)
                        </button>
                      )}
                      <motion.button
                        className="w-10 h-10 bg-gray-100 text-gray-600 hover:text-gray-800 rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Share2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-200 p-12 text-center"
            >
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Requests Found</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                There are currently no blood donation requests matching your criteria in your area.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <motion.button
                  onClick={fetchAvailableRequests}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RefreshCw className="w-5 h-5" />
                  Refresh Requests
                </motion.button>
                <motion.button
                  onClick={() => setFilters({ bloodType: '', urgency: '' })}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Filter className="w-5 h-5" />
                  Clear Filters
                </motion.button>
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
};

export default AvailableRequests;